import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Typography, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import usePrevious from '../../../hooks/usePrevious';
import Needle from '../../utils/Neddle';

import { useStyles } from './styles';

const WIDTH = 360;
const HEIGHT = 240;

interface ICPProps {
  series: number[];
  value: number;
  duration: number;
  width?: number;
  height?: number;
}

const AverageQualityChart: React.FC<ICPProps> = ({
  series,
  value,
  duration,
  width,
  height,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const [count, setCount] = useState('0');
  const prevCount = usePrevious(count);
  const startAngle = -130;
  const endAngle = 130;
  const maxValue = series.reduce((val, acc) => val + acc, 0);
  const angle = (value / maxValue) * (endAngle - startAngle) - 130;

  const options: ApexOptions = {
    chart: {
      type: 'donut',
    },
    labels: [
      t('screens:assetsHome.critical'),
      t('screens:assetsHome.degraded'),
      t('screens:assetsHome.altered'),
      t('screens:assetsHome.normal'),
    ],
    plotOptions: {
      pie: {
        customScale: 1.0,
        expandOnClick: false,
        startAngle,
        endAngle,
        donut: {
          size: '80%',
          labels: {
            show: false,
            value: {
              show: false,
            },
          },
        },
      },
    },
    stroke: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      show: true,
      floating: true,
      position: 'bottom',
      markers: {
        radius: 0,
      },
    },
    colors: [
      theme.charts.urgent,
      theme.charts.high,
      theme.charts.average,
      theme.charts.low,
    ],
  };

  useEffect(() => {
    let start = 0;
    const end = parseInt(String(value).substring(0, 3), 10);
    if (value === 0) {
      setCount(String(0));
      return;
    }
    if (start === end) return;

    const incrementTime = (Number(duration) / end) * 1000;

    const timer = setInterval(() => {
      if (start > end) {
        start -= 1;
      } else {
        start += 1;
      }
      setCount(String(start) + String(value).substring(3));
      if (start === end) clearInterval(timer);
    }, incrementTime);
  }, [value, duration]);

  return (
    <div
      style={{
        width: WIDTH,
        height: HEIGHT,
        marginBottom: 80,
      }}
    >
      <Typography variant="subtitle1" className={classes.title}>
        {t('screens:assetsHome.averageQualityOfRoads')}
      </Typography>
      <Chart options={options} series={series} type="donut" />
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 90,
          width: WIDTH / 2,
          height: WIDTH / 2,
          borderRadius: WIDTH / 4,
          zIndex: 10,
          borderStyle: 'dashed',
          borderBottomColor: 'transparent',
          borderTopColor: '#C4C6C9',
          borderLeftColor: '#C4C6C9',
          borderRightColor: '#C4C6C9',
        }}
      />
      <Needle
        style={{
          position: 'absolute',
          bottom: 68,
          left: WIDTH / 2 - 10,
          zIndex: 10,
          transformOrigin: 'bottom',
          transform: `rotate(${angle}deg)`,
          transitionDuration: `${duration}s`,
          transitionTimingFunction: 'ease-in-out',
        }}
      />
      <div className={classes.valueContainer}>
        <Typography variant="body1" className={classes.value}>
          {`${t('screens:assetsHome.rci')} = ${count}`}
        </Typography>
      </div>
    </div>
  );
};

export default AverageQualityChart;
